
export type AnimationID = number;

export type EasingFunc = "linear" | "ease-in" | "ease-out" | "ease-in-out";

export interface Animation {
	/**
	 * Animating function
	 * @param t  easing parameter from 0..1
	 * @param a  reference to this animation
	 * @returns  true if the animation should terminate before completion. (false = animation will continue, next call will be scheduled)
	 */
	animate: (t: number, a: Animation) => boolean;
	/** Run this handler after animation completed */
	finish?: (a: Animation) => void;
	/** Number of milliseconds */
	duration: number;
	/** Inital way before starting animation */
	delay?: number;
	/** Easing function: linear | ease-in | ease-out | ease-in-out */
	easing?: EasingFunc;
	/** This animation will start after specified animation ends. If not specified or ID is invalid, it will start immediately */
	follow?: AnimationID;

	/** Set to true if the animation require re-layout of components */
	layout?: boolean;
}

export type AnimationState = "next" | "waiting" | "running" | "completed";

interface AnimationInternal extends Animation {
	id: AnimationID;
	state: AnimationState;
	/** timestamp of the start */
	startAt?: number;
	/** timestamp of the end */
	endAt?: number;
}

export interface AnimateResult {
	needNextFrame: boolean;
	needLayout: boolean;
}

export class AnimationEngine {
	private refresh: () => void;
	private running: AnimationInternal[];
	private waiting: AnimationInternal[];
	private next: AnimationInternal[];
	private nextID:AnimationID;
	private currentDelayTimer:number;

	constructor(refreshFunction:() => void) {
		this.refresh = refreshFunction;
		this.running = [];
		this.waiting = [];
		this.next = [];
		this.nextID = 1;
		this.currentDelayTimer = 0;
	}

	private toInternal(a:Animation, state:AnimationState):AnimationInternal {
		return {
			...a,
			id: this.nextID++,
			state: state
		};
	}

	updateDelayTimer() {
		if (this.currentDelayTimer) clearTimeout(this.currentDelayTimer);
		// Find next tick
		let nextTick = -1;
		for (let a of this.waiting) {
			if (nextTick < 0 || a.startAt < nextTick) nextTick = a.startAt;
		}
		if (nextTick >= 0) {
			const delayToFirst = nextTick - Date.now();
			this.currentDelayTimer = setTimeout(() => {
				this.currentDelayTimer = 0;
				const now = Date.now();
				let moved = 0;
				for (let i = 0; i < this.waiting.length; i++) {
					const a = this.waiting[i];
					// Move waiting to running
					if (a.startAt <= now) {
						a.endAt = a.startAt + a.duration
						this.waiting.splice(i--, 1);
						this.running.push(a);
						moved++;
					}
				}
				//this.log("delay timer");
				if (moved > 0) this.refresh();
				this.updateDelayTimer();	// schedule the next delay timer
			}, delayToFirst);
		}
	}

	addNext(a: Animation, existing:AnimationInternal):AnimationID {
		let ia = this.toInternal(a, "next");
		this.next.push(ia);
		//this.log("add next");
		return ia.id;
	}

	addDelayed(a: Animation):AnimationID {
		let ia = this.toInternal(a, "waiting");
		ia.startAt = Date.now() + a.delay;
		this.waiting.push(ia);
		this.updateDelayTimer();
		//this.log("add delayed");
		return ia.id;
	}

	addRunning(a: Animation):AnimationID {
		let ia = this.toInternal(a, "running");
		ia.startAt = Date.now();
		ia.endAt = ia.startAt + a.duration;
		this.running.push(ia);
		this.refresh();
		//this.log("add running");
		return ia.id;
	}

	easing(t: number, easingFunc?:EasingFunc):number {
		if (easingFunc && easingFunc !== "linear") {
			switch (easingFunc) {
				case "ease-in":
					return t * t;
				case "ease-out":
					return t * (2 - t);
				case "ease-in-out":
					return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
			}
		}
		return t;
	}

	animationFinished(a: AnimationInternal):boolean {
		// Remove this animation from array of running animations
		this.removeFrom(this.running, a.id);
		a.finish?.(a);
		// Schedule followers
		let newDelayed = false;
		let newRunning = false;
		for (let i = 0; i < this.next.length; i++) {
			const na = this.next[i];
			// Move waiting to running
			if (na.follow === a.id) {
				this.next.splice(i--, 1);	// remove from next
				// This one should run
				if (na.delay) {
					// will be delayed
					na.startAt = Date.now() + na.delay;
					this.waiting.push(na);
					newDelayed = true;
				} else {
					na.startAt = Date.now();
					na.endAt = na.startAt + na.duration;
					newRunning = true;
					this.running.push(na);
				}
			}
		}
		if (newDelayed) this.updateDelayTimer();
		//this.log("animationFinished");
		return newRunning;
	}

	/**
	 * Process all running animation
	 * @return true if the next animation frame is required
	 **/
	animateRunning():AnimateResult {
		const now = Date.now();
		let needMore = false;
		let needLayout: boolean = false;
		for (let i = 0; i < this.running.length; i++) {
			const a = this.running[i];
			if (!!a.layout) needLayout = true;
			let linearT = (now - a.startAt) / (a.endAt - a.startAt);
			if (linearT < 0) linearT = 0;
			else if (linearT > 1) linearT = 1;
			let t = this.easing(linearT, a.easing);
			const cancel = !!a.animate(t, a);
			if (!cancel && linearT < 1) needMore = true;
			else {
				if (this.animationFinished(a)) needMore = true;
			}
		}
		return { needLayout, needNextFrame: needMore };
	}

	logArray(name: string, arr: AnimationInternal[]) {
		if (arr.length === 0) return;
		let b = name+": [";
		for (let a of arr) {
			b += "{ id:"+a.id;
			b += " }";
		}
		b += "]";
		console.log(b);
	}

	log(msg?:string) {
		if (msg) console.log(">>> " + msg);
		console.log("timer "+(this.currentDelayTimer ? "YES":"NO"));
		this.logArray("running", this.running);
		this.logArray("waiting", this.waiting);
		this.logArray("next", this.next);
	}

	add(a: Animation):AnimationID {
		if (a.follow) {
			// This animation follows another animation
			const existing = this.findAnimation(a.follow);
			if (existing != null && existing.state !== "completed") {
				return this.addNext(a, existing);
			}
		}
		// This animation doesn't follow any other animation. it can be waiting or running
		if (a.delay) {
			// It is delayed
			return this.addDelayed(a);
		}
		const rid = this.addRunning(a);
		return rid;
	}

	findAnimation(aid: AnimationID):AnimationInternal|null {
		for (let a of this.running) {
			if (a.id === aid) return a;
		}
		for (let a of this.waiting) {
			if (a.id === aid) return a;
		}
		for (let a of this.next) {
			if (a.id === aid) return a;
		}
	}

	private removeFrom(animations:AnimationInternal[], id:AnimationID):void {
		for (let i = 0; i < animations.length; i++) {
			const a = animations[i];
			if (a.id === id) {
				animations.splice(i, 1);
				break;
			}
		}
	}

	cancel(id?: AnimationID) {
		if (id) {
			this.removeFrom(this.next, id);
			this.removeFrom(this.waiting, id);
			this.removeFrom(this.running, id);
		} else {
			this.next = [];
			this.waiting = [];
			this.running = [];
		}
		this.updateDelayTimer();
	}
}